import update from 'immutability-helper';
import {isEmpty} from 'lodash';
import {useMemo} from 'react';

import {DashboardPanelData} from '../../../interfaces/Dashboard';
import {
  injectDashboardDates,
  useDashboardDates,
} from '../../../utils/dashboardDate';
import {
  BeltHistory,
  BeltHistoryReportData,
  getBeltHistoryReportData,
} from './BeltHistoryReport';

interface Props {
  value?: DashboardPanelData;
  onUpdate?: (value?: DashboardPanelData) => void;
}

export interface BeltTrackingReportData {
  viewType: 'beltHistoryReport';
  title: string;
  beltHistoryReport: BeltHistoryReportData;
}

const getBeltTrackingReports = (): BeltTrackingReportData => ({
  viewType: 'beltHistoryReport',
  title: 'Belt History Report',
  beltHistoryReport: getBeltHistoryReportData(),
});

export const BeltHistoryReport = ({value, onUpdate}: Props) => {
  const config = useMemo(
    () =>
      !isEmpty(value)
        ? (value as BeltTrackingReportData)
        : getBeltTrackingReports(),
    [value]
  );

  const dashboardDates = useDashboardDates(value?.detachedFromDashboardDate);

  const beltHistoryValue = useMemo(() => {
    const base = config.beltHistoryReport;
    if (!dashboardDates) {
      return base;
    }
    const newItems = injectDashboardDates(base.openedItems, dashboardDates);
    if (newItems === base.openedItems) {
      return base;
    }
    return {...base, openedItems: newItems};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    config.beltHistoryReport,
    dashboardDates?.date_start,
    dashboardDates?.date_end,
  ]);

  return (
    <BeltHistory
      value={beltHistoryValue}
      onUpdate={(v) => {
        let detach = false;
        if (
          dashboardDates &&
          v?.openedItems &&
          v.openedItems.length === config.beltHistoryReport.openedItems.length
        ) {
          const activeIdx = v.openedItems.findIndex(
            (it) => it.id === (v.activeId ?? config.beltHistoryReport.activeId)
          );
          const incoming =
            activeIdx >= 0 ? v.openedItems[activeIdx] : undefined;
          if (
            incoming &&
            (incoming.params?.date_start !== dashboardDates.date_start ||
              incoming.params?.date_end !== dashboardDates.date_end)
          ) {
            detach = true;
          }
        }
        const base = update(value ?? getBeltTrackingReports(), {
          beltHistoryReport: {$set: v ?? getBeltHistoryReportData()},
        });
        onUpdate?.(detach ? {...base, detachedFromDashboardDate: true} : base);
      }}
    />
  );
};
